import { Component, ViewChild } from '@angular/core';
import { GroupTypeIds, TokenService } from '@auth';
import {
  PDFViewerComponent,
  PDFViewerLoadEvent,
  PDFViewerPageChangeEvent,
} from '@progress/kendo-angular-pdfviewer';
import { BehaviorSubject, timeout } from 'rxjs';
import { AlertsService } from '@shared-data';
import { MessageService } from '@shared';

@Component({
  selector: 'app-terms-of-service',
  templateUrl: './terms-of-service.component.html',
  styleUrl: './terms-of-service.component.scss',
})
export class TermsOfServiceComponent {
  @ViewChild(PDFViewerComponent, { static: true })
  pdfViewer!: PDFViewerComponent;
  pdfUrl = '/assets/pdf/terms-of-service.pdf';

  totalPages: boolean[] = [false];
  agreed = false;
  isMember$ = new BehaviorSubject<boolean>(false);

  constructor(
    private tokenService: TokenService,
    private alertsService: AlertsService,
    private messageService: MessageService,
  ) {}

  async ngOnInit() {
    const userName = await this.tokenService.getTokenAttribute('sub');
    this.agreed = sessionStorage.getItem(`tos_${userName}`) === 'true';
    const groupTypeId = await this.tokenService.getTokenAttribute('GroupTypeId');
    const isMember =
      groupTypeId === GroupTypeIds.Member ||
      groupTypeId === GroupTypeIds.Canadian;

    if (!isMember || this.agreed) {
      this.isMember$.next(isMember);
      return;
    }

    // The dialog is shown only once the stored acceptance has been read, otherwise
    // it flashes on screen on every page load for the users who already opted out.
    this.alertsService
      .hasAcceptedTerms()
      .pipe(timeout(5000))
      .subscribe({
        next: (accepted) => {
          this.agreed = accepted;
          this.isMember$.next(true);
        },
        // If the acceptance cannot be read, keep showing the dialog: it is safer to
        // ask again than to hide the terms from someone who never accepted them. The
        // timeout covers a request that never answers, which would otherwise leave
        // the dialog hidden for good.
        error: () => this.isMember$.next(true),
      });
  }

  onLoad(pdf: PDFViewerLoadEvent) {
    this.totalPages = Array(pdf.context.pdfPages.length).fill(false);
  }

  onPageChange(page: PDFViewerPageChangeEvent) {
    this.totalPages[page.currentPage - 1] = true;
    this.totalPages[page.previousPage - 1] = true;
  }

  async onAgree(dontShowAgain: boolean) {
    this.agreed = true;
    const userName = await this.tokenService.getTokenAttribute('sub');
    sessionStorage.setItem(`tos_${userName}`, 'true');

    if (!dontShowAgain) return;

    this.alertsService.acceptTerms().subscribe({
      error: () => {
        this.messageService.error(
          'Your acceptance could not be saved, the terms will be shown again on your next login',
        );
      },
    });
  }

  get allPagesRead() {
    return this.totalPages.every((page) => page);
  }
}
